// ============================================================================
// DUPLIQUER UN ARTICLE — la fenêtre courte de la fiche (maquette : `dupVals`/`dupGo`).
//
// UTILITÉ : la même chemise dans une autre matière, le même parfum dans un autre format. La copie reprend tout ce que
// porte la fiche (décision utilisateur : « tous les champs possibles et imaginables, les prix aussi »), selon trois
// interrupteurs, avec les couleurs qu'on garde ; elle naît avec des codes-barres neufs et s'ouvre aussitôt.
//
// ⚠️ LES CASES SONT CELLES DE L'ARTICLE D'ORIGINE, pas toute la grille : une grille de quinze tailles dont l'article n'en
//    porte que cinq ferait naître dix déclinaisons que personne n'a demandées.
// ⚠️ LE SERVEUR ÉCRIT TOUT EN UNE TRANSACTION et refuse un code article déjà porté (`duplicateModel`) ; ce qui est dit
//    ici l'annonce, le serveur décide.
// ⚠️ « FOURNISSEUR — À SCANNER » N'EST PAS ENCORE CONSTRUIT (décision D5 : la copie s'ouvrira non enregistrée, chaque code
//    bipé créant sa case). Le choix est montré inactif avec son motif, jamais offert sans effet (§23.0).
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { proposeArticleCode } from '@orgazone/core-catalog';
import { Icon, Modal, OZ_INTERACTIVE_SURFACE, rovingRadio, Switch } from '@orgazone/ui-web';
import { api, type ArticleVariant } from '../api';
import { t } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { useArticleCodeCheck } from './article-code-check';

/** La copie se crée en une fois : au-delà, le serveur refuse le lot (`VARIANTS_CREATE_MAX`). */
const DUPLICATE_VARIANTS_MAX = 100;
/** Longueur maximale d'un code saisi — celle que le serveur accepte (`DUPLICATE_CODE_MAX`). */
const CODE_MAX = 40;

/** Ce que la fiche sait de l'article d'origine. */
export interface DuplicateSource {
  modelKey: string;
  name: string;
  articleCode: string | null;
  family: string | null;
}

/** Ce que la fiche ouvre une fois la copie écrite. */
export interface DuplicateResult {
  modelKey: string;
  name: string;
  variantCount: number;
  gencodes: string[];
  forSale: boolean;
  family: string | null;
  /** « « Chemise oxford » · CHE-OXF » — l'article d'origine, pour le bandeau de la copie. */
  sourceLabel: string;
}

/** Une case de la copie. */
interface CopyCase { color: string | null; size: string | null; size2: string | null }

/**
 * LES CASES DE LA COPIE — celles de l'article d'origine pour les couleurs gardées ; sans couleur gardée, une case par
 * taille (l'article naît sans couleur). ⚠️ FONCTION PURE, exportée pour son banc.
 */
export function copyCases(rows: readonly Pick<ArticleVariant, 'color' | 'size' | 'size2'>[], kept: ReadonlySet<string>): CopyCase[] {
  const hasColors = rows.some((r) => (r.color ?? '').trim() !== '');
  const seen = new Set<string>();
  const out: CopyCase[] = [];
  for (const r of rows) {
    const color = hasColors && kept.size > 0 ? (r.color ?? '').trim() || null : null;
    if (hasColors && kept.size > 0 && (color === null || !kept.has(color))) continue;
    const c = { color, size: r.size?.trim() || null, size2: r.size2?.trim() || null };
    const k = `${(c.color ?? '').toLowerCase()}|${(c.size ?? '').toLowerCase()}|${(c.size2 ?? '').toLowerCase()}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

export function DupliquerArticle({ open, source, onClose, onCreated }: {
  open: boolean;
  source: DuplicateSource;
  onClose: () => void;
  onCreated: (r: DuplicateResult) => void;
}) {
  const [name, setName] = useState(source.name);
  /** Le code saisi à la main — vide tant qu'il suit la désignation. */
  const [typed, setTyped] = useState('');
  /** La base du code proposé : celui de l'article d'origine, puis celui de la désignation dès qu'on la change. */
  const [base, setBase] = useState(source.articleCode ?? proposeArticleCode(source.name));
  const [take, setTake] = useState({ classification: true, prices: true, custom: true });
  const [rows, setRows] = useState<ArticleVariant[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [hasCustom, setHasCustom] = useState<boolean | null>(null);
  const [kept, setKept] = useState<ReadonlySet<string>>(new Set());
  const [readFailure, setReadFailure] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // ⚠️ UNE CLÉ PAR OUVERTURE : un double clic ne fait pas deux copies.
  const [idempotencyKey, setIdempotencyKey] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(source.name); setTyped(''); setBase(source.articleCode ?? proposeArticleCode(source.name));
    setTake({ classification: true, prices: true, custom: true });
    setRows(null); setTruncated(false); setHasCustom(null); setReadFailure(null); setFailure(null);
    setIdempotencyKey(`dup:${source.modelKey}:${crypto.randomUUID()}`);
    let vivant = true;
    api.articleVariants(source.modelKey)
      .then((v) => {
        if (!vivant) return;
        setRows(v.rows); setTruncated(v.truncated);
        setKept(new Set(v.rows.map((r) => (r.color ?? '').trim()).filter(Boolean)));
      })
      .catch((e: unknown) => { if (vivant) setReadFailure(e instanceof Error ? e.message : String(e)); });
    api.articleCustomValues(source.modelKey)
      .then((c) => { if (vivant) setHasCustom(Object.values(c.values).some((v) => String(v ?? '').trim() !== '')); })
      // ⚠️ Best-effort ASSUMÉ : sans lecture, l'interrupteur reste actif et le serveur recopie ce qui existe — ou rien.
      .catch(() => { if (vivant) setHasCustom(true); });
    return () => { vivant = false; };
  }, [open, source.modelKey, source.name, source.articleCode]);

  const check = useArticleCodeCheck(open, name, typed, typed ? undefined : base);
  const colors = useMemo(
    () => [...new Set((rows ?? []).map((r) => (r.color ?? '').trim()).filter(Boolean))],
    [rows],
  );
  const cases = useMemo(() => copyCases(rows ?? [], kept), [rows, kept]);
  const keptCount = colors.filter((c) => kept.has(c)).length;
  const sizes = new Set(cases.map((c) => `${c.size ?? ''}|${c.size2 ?? ''}`)).size;

  const nameOk = name.trim() !== '';
  const codeTaken = typed.trim() !== '' && check.freeAlternative !== null;
  const blocked = !nameOk ? t('La copie se nomme : saisissez une désignation.')
    : !check.effective ? t('Saisissez un code article.')
    : codeTaken ? t('Ce code article est déjà porté.')
    : check.blocks ? t('Vérification du code article…')
    : readFailure ? t('Les déclinaisons de l’article d’origine sont illisibles : {motif}', { motif: readFailure })
    : !rows ? t('Lecture des déclinaisons…')
    : truncated || cases.length > DUPLICATE_VARIANTS_MAX ? t('Plus de {n} déclinaisons : la copie se crée en une fois, {n} au plus.', { n: DUPLICATE_VARIANTS_MAX })
    : cases.length === 0 ? t('Aucune déclinaison à créer.')
    : null;

  const create = async () => {
    if (blocked || writing) return;
    setWriting(true);
    setFailure(null);
    try {
      const r = await api.duplicateArticle({
        source: source.modelKey, name: name.trim(), code: check.effective, take, idempotencyKey,
        variants: cases.map((c) => ({ color: c.color, size: c.size, size2: c.size2 })),
      });
      onCreated({
        modelKey: r.modelKey, name: name.trim(), variantCount: cases.length, gencodes: r.gencodes, forSale: r.forSale,
        family: take.classification ? source.family : null,
        sourceLabel: `« ${source.name} »${source.articleCode ? ` · ${source.articleCode}` : ''}`,
      });
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setWriting(false);
    }
  };

  const summary = (() => {
    const values = {
      declinaisons: t('{n} déclinaison', { n: cases.length }),
      codes: t('{n} code-barres généré', { n: cases.length }),
    };
    if (keptCount > 0 && cases.length === keptCount * sizes) {
      return t('{couleurs} × {tailles} = {declinaisons} · stock 0 · {codes}', {
        ...values, couleurs: t('{n} couleur', { n: keptCount }), tailles: t('{n} taille', { n: sizes }),
      });
    }
    return t('{declinaisons} · stock 0 · {codes}', values);
  })();

  const mono = { fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase' as const, color: 'var(--ink-500)', fontWeight: 600 };
  const label = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 };
  const input = {
    width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box' as const, borderRadius: 'var(--r-ctrl)',
    background: 'var(--paper)', color: 'var(--ink-900)', outline: 'none', border: '1px solid var(--ink-200)', fontWeight: 600,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      icon="copy"
      tone="teal"
      title={t('Dupliquer l’article')}
      subtitle={t('À partir de {source}', { source: `« ${source.name} »${source.articleCode ? ` · ${source.articleCode}` : ''}` })}
      // ⚠️ Une fenêtre ne se ferme pas PENDANT l'écriture : on ne saurait pas dire si la copie existe.
      blockClose={() => writing}
      footer={(
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10, width: '100%' }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={writing}>{t('Annuler')}</button>
          <button
            type="button" className="btn-cta" aria-disabled={!!blocked || writing} title={blocked ?? undefined}
            onClick={() => { if (!blocked && !writing) void create(); }}
          >{writing ? t('Création…') : t('Créer et ouvrir la fiche')}</button>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {failure && (
          <p role="alert" style={{
            margin: 0, padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)',
            border: '1px solid var(--rose-fg)', fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.5,
          }}>{failure}</p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)', gap: 10, alignItems: 'start' }}>
          <label style={{ display: 'block', minWidth: 0 }}>
            <span style={label}>{fieldLabel('name')}</span>
            <input
              value={name} placeholder={source.name} autoComplete="off" style={{ ...input, fontSize: '13.5px' }}
              onChange={(e) => {
                setName(e.target.value);
                // ⚠️ LE CODE SUIT LA DÉSIGNATION tant qu'on ne l'a pas retouché (même règle que l'assistant de création).
                if (!typed) setBase(proposeArticleCode(e.target.value));
              }}
            />
          </label>
          <label style={{ display: 'block', minWidth: 0 }}>
            <span style={label}>{fieldLabel('articleCode')}</span>
            <input
              value={typed || check.proposed} autoComplete="off" spellCheck={false} maxLength={CODE_MAX}
              style={{ ...input, fontFamily: 'var(--font-mono)', fontSize: '12.5px', borderColor: codeTaken ? 'var(--rose-fg)' : 'var(--ink-200)' }}
              onChange={(e) => setTyped(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, CODE_MAX))}
            />
            {codeTaken && check.holder
              ? <span role="alert" style={{ display: 'block', marginTop: 5, fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>
                {t('Ce code est celui de « {nom} ».', { nom: check.holder.name })}</span>
              : <span style={{ display: 'block', marginTop: 5, fontSize: '11px', color: 'var(--ink-500)' }}>
                {typed ? t('Saisi à la main') : t('Suit la désignation — modifiable')}</span>}
          </label>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={mono}>{t('Reprendre')}</span>
          <Switch layout="card" checked={take.classification} onChange={(v) => setTake((p) => ({ ...p, classification: v }))}
            label={t('Classement')} hint={t('Marque, fournisseur, secteur, famille, saison, collection')} />
          <Switch layout="card" checked={take.prices} onChange={(v) => setTake((p) => ({ ...p, prices: v }))}
            label={t('Prix & marge')} hint={take.prices
              ? t('PA, PV, coef, ferme ou corner, prix par déclinaison')
              : t('Sans ses prix, la copie naît retirée de la vente : zéro euro se vendrait gratuitement.')} />
          <Switch layout="card" checked={take.custom && hasCustom !== false} disabled={hasCustom === false}
            onChange={(v) => setTake((p) => ({ ...p, custom: v }))}
            label={t('Champs perso')} hint={hasCustom === false
              ? t('Aucun champ renseigné sur l’article d’origine')
              : t('Les valeurs saisies sur l’article d’origine')} />
          <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.45 }}>
            {t('La grille de tailles est toujours reprise — elle se change ensuite dans la fiche.')}</span>
        </div>

        {colors.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={mono}>{t('Couleurs · {k} sur {n}', { k: keptCount, n: colors.length })}</span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                <button type="button" className="btn-ghost" style={{ height: 26, padding: '0 8px', fontSize: '12px', color: 'var(--teal-800)' }}
                  onClick={() => setKept(new Set(colors))}>{t('Toutes')}</button>
                <button type="button" className="btn-ghost" style={{ height: 26, padding: '0 8px', fontSize: '12px' }}
                  onClick={() => setKept(new Set())}>{t('Aucune')}</button>
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
              {colors.map((c) => {
                const on = kept.has(c);
                return (
                  <button
                    key={c} type="button" role="checkbox" aria-checked={on} className={OZ_INTERACTIVE_SURFACE}
                    onClick={() => setKept((p) => { const n = new Set(p); if (on) n.delete(c); else n.add(c); return n; })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9, height: 38, padding: '0 11px', minWidth: 0, textAlign: 'left',
                      border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)',
                      background: on ? 'var(--teal-50)' : 'var(--paper)', cursor: 'pointer',
                    }}
                  >
                    <span aria-hidden style={{
                      width: 16, height: 16, borderRadius: 'var(--r-xs)', flexShrink: 0, boxSizing: 'border-box',
                      border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: on ? 'var(--teal-500)' : 'var(--paper)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--paper)',
                    }}>{on && <Icon name="check" size={10} stroke={3.4} />}</span>
                    <span style={{ minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: on ? 'var(--teal-800)' : 'var(--ink-700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</span>
                  </button>
                );
              })}
            </div>
            {keptCount === 0 && (
              <span style={{ fontSize: '11.5px', color: 'var(--ink-600)', lineHeight: 1.45 }}>
                {t('Aucune couleur reprise : l’article est créé sans couleur — ajoutez-en ensuite dans Déclinaisons.')}</span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={mono}>{fieldLabel('gencode')}</span>
          <span role="radiogroup" aria-label={fieldLabel('gencode')} className="seg-track" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <button type="button" role="radio" aria-checked className="seg active"
              {...rovingRadio({ index: 0, count: 2, checked: true, anyChecked: true, onPick: () => undefined, disabled: (i) => i === 1 })}>
              {t('Générés automatiquement')}</button>
            {/* ⚠️ INACTIF, ET IL DIT POURQUOI : la copie non enregistrée (D5) n'est pas construite. */}
            <button type="button" role="radio" aria-checked={false} aria-disabled className="seg"
              {...rovingRadio({ index: 1, count: 2, checked: false, anyChecked: true, onPick: () => undefined, disabled: (i) => i === 1 })}
              title={t('Bientôt : la copie s’ouvrira sans être enregistrée, et chaque code bipé créera sa case.')}
              style={{ color: 'var(--ink-400)', cursor: 'not-allowed' }}>
              {t('{fournisseur} — à scanner', { fournisseur: fieldLabel('supplier') })}</button>
          </span>
          <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.45 }}>
            {t('Un code-barres neuf par déclinaison, au préfixe du magasin — jamais ceux de l’article d’origine.')}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--teal-50)' }}>
          <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--teal-800)', lineHeight: 1.4 }}>{rows ? summary : t('Lecture des déclinaisons…')}</span>
          <span style={{ fontSize: '11.5px', color: 'var(--ink-600)', lineHeight: 1.45 }}>
            {t('Jamais repris : codes-barres et code modèle du fournisseur, photos, stock, ventes et mouvements, commandes et BL, place dans les articles rapides.')}</span>
        </div>
      </div>
    </Modal>
  );
}
